import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from './AxiosBaseQuery';
import { API_SERVER_URL, V3_APP_BASE_URL } from '@/constants/Config';

export interface IVoiceNoteSignedURLReq {
  path: string;
  name: string;
  type: string;
  size: number;
}

export interface IVoiceNoteSignedURLRes {
  data: {
    fileName?: string;
    signedRequest?: string;
    url?: string;
  };
  message?: string;
}

export interface IUploadAudioReq {
  file: Blob;
  fileType: string;
  url: string;
}

export interface IErrRes {
  error: unknown;
  data?: { message?: string };
}

export interface S3SignedUrlResponse {
  fileName: string;
  signedRequest: string;
  url: string;
}

export interface IPublicPreSignedUrlRequest {
  fileName: string;
  fileSize: number;
  fileType: string;
  folder: string;
  public: boolean;
}

export interface IPublicPreSignedUrlApiResponse {
  data?: S3SignedUrlResponse;
  fileName?: string;
  signedRequest?: string;
  url?: string;
}

export const s3QueryApi = createApi({
  reducerPath: 's3Query',
  baseQuery: axiosBaseQuery({
    baseUrl: V3_APP_BASE_URL,
  }),
  tagTypes: ['VoiceNote'],
  endpoints: (build) => ({
    getPreSignedURL: build.mutation({
      query: (data) => ({
        url: 'api/cp/voice-note/get-pre-signed-url',
        method: 'POST',
        data: { ...data },
      }),
      invalidatesTags: ['VoiceNote'],
      transformResponse: (response: IVoiceNoteSignedURLRes) => response,
      transformErrorResponse: (response: { error: IErrRes }) => response,
    }),
    uploadAudio: build.mutation({
      query: (data: IUploadAudioReq) => ({
        url: data.url,
        method: 'put',
        data: { ...data },
        headers: {
          'x-amz-acl': 'private',
          'Content-Type': data.fileType,
          'Sec-Fetch-Dest': 'empty',
          'Sec-Fetch-Mode': 'cors',
          'Sec-Fetch-Site': 'cross-site',
        },
      }),
    }),
    getS3PreSignedUrl: build.mutation<S3SignedUrlResponse, FormData>({
      query: (data) => ({
        url: 'api/v4/s3',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['VoiceNote'],
    }),
    getPublicPreSignedUrl: build.mutation<S3SignedUrlResponse, IPublicPreSignedUrlRequest>({
      query: (data) => ({
        url: `${API_SERVER_URL}api/public/get-pre-signed-url`,
        method: 'POST',
        data,
      }),
      transformResponse: (response: IPublicPreSignedUrlApiResponse): S3SignedUrlResponse => {
        const payload = response.data ?? response;

        return {
          fileName: payload.fileName ?? '',
          signedRequest: payload.signedRequest ?? '',
          url: payload.url ?? '',
        };
      },
      invalidatesTags: ['VoiceNote'],
    }),
  }),
});

export const {
  useGetPreSignedURLMutation,
  useUploadAudioMutation,
  useGetS3PreSignedUrlMutation,
  useGetPublicPreSignedUrlMutation,
} = s3QueryApi;
